import { create } from 'zustand';

const OPEN_KEY = 'codeframe.panel.open';
const WIDTH_KEY = 'codeframe.panel.width';
const RATIO_KEY = 'codeframe.panel.chatRatio';

/** Gap (px) between the floating panel card and the screen edges. */
export const PANEL_INSET = 12;

export const MIN_WIDTH = 260;
export const MAX_WIDTH = 720;
export const DEFAULT_WIDTH = 300;

export const MIN_RATIO = 0.2;
export const MAX_RATIO = 0.7;
export const DEFAULT_RATIO = 0.4;

/** Clamp a panel width to the sane resize range. Pure — unit-tested. */
export function clampWidth(n: number): number {
  if (Number.isNaN(n)) return DEFAULT_WIDTH;
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(n)));
}

/** Clamp the chat-region height fraction. Pure — unit-tested. */
export function clampRatio(n: number): number {
  if (Number.isNaN(n)) return DEFAULT_RATIO;
  return Math.min(MAX_RATIO, Math.max(MIN_RATIO, n));
}

function readOpen(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(OPEN_KEY) === '1';
}
function readWidth(): number {
  if (typeof localStorage === 'undefined') return DEFAULT_WIDTH;
  const v = Number(localStorage.getItem(WIDTH_KEY));
  return v ? clampWidth(v) : DEFAULT_WIDTH;
}
function readRatio(): number {
  if (typeof localStorage === 'undefined') return DEFAULT_RATIO;
  const v = Number(localStorage.getItem(RATIO_KEY));
  return v ? clampRatio(v) : DEFAULT_RATIO;
}
function persist(key: string, value: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(key, value);
  } catch {
    /* quota / disabled */
  }
}

interface PanelStore {
  open: boolean;
  width: number;
  chatRatio: number;
  /** Ephemeral un-scope: chat scope follows the selection; true forces the
   *  whole-canvas thread until the selection changes. Never persisted. */
  chatGlobalOverride: boolean;
  /** Whether the code/script editor region is expanded. Ephemeral (not
   *  persisted): the panel opens chat-first; the editor shows only when the
   *  user opens it intentionally (context menu "Edit in panel" / Code toggle). */
  editorOpen: boolean;
  /** Whether the Checkpoints modal is open. Ephemeral (not persisted). */
  checkpointsOpen: boolean;
  /** Whether the Connect-agent (MCP pairing) modal is open. Ephemeral. */
  connectAgentOpen: boolean;
  /** Whether the Publish modal is open. Ephemeral (not persisted). */
  publishOpen: boolean;
  toggle: () => void;
  setOpen: (open: boolean) => void;
  setWidth: (width: number) => void;
  setChatRatio: (ratio: number) => void;
  setChatGlobalOverride: (on: boolean) => void;
  setEditorOpen: (open: boolean) => void;
  setCheckpointsOpen: (open: boolean) => void;
  setConnectAgentOpen: (open: boolean) => void;
  setPublishOpen: (open: boolean) => void;
}

export const usePanelStore = create<PanelStore>((set) => ({
  open: readOpen(),
  width: readWidth(),
  chatRatio: readRatio(),
  chatGlobalOverride: false,
  editorOpen: false,
  checkpointsOpen: false,
  connectAgentOpen: false,
  publishOpen: false,
  toggle: () =>
    set((s) => {
      const open = !s.open;
      persist(OPEN_KEY, open ? '1' : '0');
      return { open };
    }),
  setOpen: (open) => {
    persist(OPEN_KEY, open ? '1' : '0');
    set({ open });
  },
  setWidth: (width) => {
    const w = clampWidth(width);
    persist(WIDTH_KEY, String(w));
    set({ width: w });
  },
  setChatRatio: (ratio) => {
    const r = clampRatio(ratio);
    persist(RATIO_KEY, String(r));
    set({ chatRatio: r });
  },
  setChatGlobalOverride: (on) => set({ chatGlobalOverride: on }),
  setEditorOpen: (open) => set({ editorOpen: open }),
  setCheckpointsOpen: (open) => set({ checkpointsOpen: open }),
  setConnectAgentOpen: (open) => set({ connectAgentOpen: open }),
  setPublishOpen: (open) => set({ publishOpen: open }),
}));
